import React, {useMemo, useState} from 'react';
import {Badge, Button, Card, Checkbox, Input, message, Popconfirm, Popover, Radio, Select, Space, Table, Tag} from 'antd';
import {useOpenApiFpRequest} from '../../Http/useOpenApiRequest';
import {SubmissionApi, SubmissionDto, SubmissionStatus, TermApi, UserApi, UserRole} from '../../scaffold';
import {useMount} from 'react-use';
import DateFormat from '../../utils/DateFormat';
import SubmissionStatusTag from '../../components/Submission/SubmissionStatusTag';
import {useHistory} from 'react-router-dom';
import useAuth from '../../utils/AuthContext';
import SubmissionCreateBtn from '../../components/Submission/CreateBtn';
import {useBoolean, useInput} from 'react-hanger';
import CommonApiErrorHandler from '../../utils/HttpInstance';
import Flex from '../../components/Flex';
import Text from 'antd/lib/typography/Text';

export default function SubmissionListPage() {
  const auth = useAuth();
  const keyword = useInput('');
  const listState = useOpenApiFpRequest(SubmissionApi, SubmissionApi.prototype.submissionListGet);
  const revokeState = useOpenApiFpRequest(SubmissionApi, SubmissionApi.prototype.submissionRevokePost);
  const removeState = useOpenApiFpRequest(SubmissionApi, SubmissionApi.prototype.submissionRemovePost);
  const sendMessage = useOpenApiFpRequest(SubmissionApi, SubmissionApi.prototype.submissionSendMessagePost);
  const distributeSupervisor = useOpenApiFpRequest(SubmissionApi, SubmissionApi.prototype.submissionDistributeSupervisorPost);
  const distributeJudge = useOpenApiFpRequest(SubmissionApi, SubmissionApi.prototype.submissionDistributeJudgerPost);
  const distributeChecker = useOpenApiFpRequest(SubmissionApi, SubmissionApi.prototype.submissionDistributeCheckerPost);
  const updateTerm = useOpenApiFpRequest(SubmissionApi, SubmissionApi.prototype.submissionUpdateTermPost);
  const history = useHistory();
  const needActionOnly = useBoolean(false);
  const [step, setStep] = useState(0);
  const [selectedRows, setSelectedRows] = useState<SubmissionDto[]>([]);
  const listUser = useOpenApiFpRequest(UserApi, UserApi.prototype.userListGet);
  const listTerm = useOpenApiFpRequest(TermApi, TermApi.prototype.termListGet);
  const isBatchDistributeSupervisor = !selectedRows.find(i => i.status !== SubmissionStatus.待分配督导);
  const isBatchDistributeJudge = !selectedRows.find(i => i.status !== SubmissionStatus.待分配盲审);
  const isBatchDistributeChecker = !selectedRows.find(i => i.status !== SubmissionStatus.待分配论文初审);
  const [termId, setTermId] = useState<number>();
  const [judgeId, setJudgeId] = useState<number>();
  const [supervisorId, setSupervisorId] = useState<number>();
  const [checkerId, setCheckerId] = useState<number>();

  function refresh() {
    setSelectedRows([]);
    listUser.requestSync({});
    listState.requestSync({});
    listTerm.requestSync({});
  }
  useMount(() => {
    refresh();
  });

  function revoke(submission: SubmissionDto) {
    revokeState
      .request({findParams: {id: submission.id || 0}})
      .then(refresh)
      .catch(CommonApiErrorHandler);
  }

  const statusOrderList: SubmissionStatus[] = [
    SubmissionStatus.待上传论文,
    SubmissionStatus.待导师审核,
    SubmissionStatus.导师审核需修改,
    SubmissionStatus.待分配督导,
    SubmissionStatus.待督导审批,
    SubmissionStatus.督导审批需修改,
    SubmissionStatus.督导审批不通过,
    SubmissionStatus.待上传盲审论文,
    SubmissionStatus.待分配论文初审,
    SubmissionStatus.待论文初审,
    SubmissionStatus.论文初审不通过,
    SubmissionStatus.待重新上传督导论文,
    SubmissionStatus.待上传盲审论文,
    SubmissionStatus.待分配盲审,
    SubmissionStatus.待盲审审批,
    SubmissionStatus.盲审审批需修改,
    SubmissionStatus.盲审审批不通过,
    SubmissionStatus.申请完成,
  ];

  const displayData = useMemo(() => {
    if (auth.currentUser?.role === UserRole.学生) return listState.data;
    const list =
      listState.data
        ?.filter(i => {
          if (!keyword.value) return true;
          return (String(i.title) + i.user?.name + i.teacher?.name + i.judge?.name + i.supervisor?.name).includes(keyword.value);
        })
        .filter(i => {
          if (!needActionOnly.value) return true;
          if (auth.currentUser?.role === UserRole.教师) {
            if (i.teacherId === auth.currentUser?.id && i.status === SubmissionStatus.待导师审核) return true;
            if (i.supervisorId === auth.currentUser?.id && i.status === SubmissionStatus.待督导审批) return true;
            if (i.judgeId === auth.currentUser?.id && i.status === SubmissionStatus.待盲审审批) return true;
            if (i.checkerId === auth.currentUser?.id && i.status === SubmissionStatus.待论文初审) return true;
          }
          if (auth.currentUser?.role === UserRole.管理员) {
            if (i.status === SubmissionStatus.待分配盲审 || i.status === SubmissionStatus.待分配督导 || i.status === SubmissionStatus.待分配论文初审)
              return true;
          }
          return false;
        })
        .filter(i => {
          if (step === 1) {
            return [SubmissionStatus.待上传论文, SubmissionStatus.待导师审核, SubmissionStatus.导师审核需修改].includes(i.status as SubmissionStatus);
          }
          if (step === 3) {
            return [
              // SubmissionStatus.待上传督导论文,
              SubmissionStatus.待分配督导,
              SubmissionStatus.待督导审批,
              SubmissionStatus.督导审批需修改,
              SubmissionStatus.督导审批不通过,
            ].includes(i.status as SubmissionStatus);
          }
          if (step === 2) {
            return [
              SubmissionStatus.待分配论文初审,
              SubmissionStatus.待论文初审,
              SubmissionStatus.待重新上传督导论文,
              SubmissionStatus.论文初审不通过,
            ].includes(i.status as SubmissionStatus);
          }
          if (step === 4) {
            return [
              SubmissionStatus.待上传盲审论文,
              SubmissionStatus.待分配盲审,
              SubmissionStatus.待盲审审批,
              SubmissionStatus.盲审审批需修改,
              SubmissionStatus.盲审审批不通过,
              SubmissionStatus.申请完成,
            ].includes(i.status as SubmissionStatus);
          }
          return true;
        }) || [];
    return [...list].sort((a, b) => {
      if (a.term?.id === b.term?.id) return statusOrderList.indexOf(a.status as any) - statusOrderList.indexOf(b.status as any);
      return (b.term?.id ?? 0) - (a.term?.id ?? 0);
    });
  }, [keyword.value, auth.currentUser, needActionOnly.value, listState.data, step]);

  return (
    <Card title={'申请列表'} extra={auth.currentUser?.role === UserRole.学生 && <SubmissionCreateBtn refresh={refresh} />}>
      <Flex className={'m-b-10'} justify={'space-between'}>
        <div>
          {(auth.currentUser?.role === UserRole.教师 || auth.currentUser?.role === UserRole.管理员) && (
            <Space>
              <Input placeholder={'输入关键字筛选'} {...keyword.eventBind} />
              <Checkbox checked={needActionOnly.value} onChange={e => needActionOnly.setValue(e.target.checked)}>
                只显示待操作
              </Checkbox>
              {auth.currentUser.role === UserRole.管理员 && (
                <Radio.Group value={step} onChange={e => setStep(e.target.value)}>
                  <Radio.Button value={0}>所有阶段</Radio.Button>
                  <Radio.Button value={1}>导师阶段</Radio.Button>
                  <Radio.Button value={2}>论文初审</Radio.Button>
                  <Radio.Button value={3}>督导阶段</Radio.Button>
                  <Radio.Button value={4}>盲审阶段</Radio.Button>
                </Radio.Group>
              )}
            </Space>
          )}
        </div>
        {auth.currentUser?.role === UserRole.管理员 && (
          <Space>
            {selectedRows.length > 0 && (
              <Popover
                title={'批量修改批次'}
                trigger={'click'}
                content={
                  <Space>
                    <Select value={termId} onChange={setTermId} style={{width: 200}} placeholder={'请选择批次'}>
                      {listTerm.data
                        ?.filter(i => !i.isDelete)
                        ?.map(u => (
                          <Select.Option key={u.id} value={u.id as number}>
                            {u.name}
                          </Select.Option>
                        ))}
                    </Select>
                    <Button
                      loading={updateTerm.loading}
                      disabled={!termId}
                      onClick={async () => {
                        try {
                          if (!termId) return;
                          for (const item of selectedRows) {
                            await updateTerm.request({updateSubmissionTermModel: {id: item.id as number, termId: termId}});
                          }
                          refresh();
                        } catch (e) {
                          message.error(e.message);
                        }
                      }}>
                      确定修改
                    </Button>
                  </Space>
                }>
                <Button>批量修改批次</Button>
              </Popover>
            )}
            {selectedRows.length > 0 && isBatchDistributeJudge && (
              <Popover
                title={'批量分配盲审'}
                trigger={'click'}
                content={
                  <Space>
                    <Select value={judgeId} onChange={setJudgeId} style={{width: 200}} placeholder={'请选择盲审'}>
                      {listUser.data
                        ?.filter(i => i.isJudge)
                        ?.map(u => (
                          <Select.Option key={u.id} value={u.id as number}>
                            {u.name}
                          </Select.Option>
                        ))}
                    </Select>
                    <Button
                      loading={distributeJudge.loading}
                      disabled={!judgeId}
                      onClick={async () => {
                        try {
                          if (!judgeId) return;
                          for (const item of selectedRows) {
                            await distributeJudge.request({submissionDistributeJudgerModel: {id: item.id, judgerId: judgeId}});
                          }
                          refresh();
                        } catch (e) {
                          message.error(e.message);
                        }
                      }}>
                      确定分配
                    </Button>
                  </Space>
                }>
                <Button>批量分配盲审</Button>
              </Popover>
            )}
            {selectedRows.length > 0 && isBatchDistributeSupervisor && (
              <Popover
                title={'批量分配督导'}
                trigger={'click'}
                content={
                  <Space>
                    <Select value={supervisorId} onChange={setSupervisorId} style={{width: 200}} placeholder={'请选择督导'}>
                      {listUser.data
                        ?.filter(i => i.isSupervisor)
                        ?.map(u => (
                          <Select.Option key={u.id} value={u.id as number}>
                            {u.name}
                          </Select.Option>
                        ))}
                    </Select>
                    <Button
                      loading={distributeSupervisor.loading}
                      disabled={!supervisorId}
                      onClick={async () => {
                        try {
                          if (!supervisorId) return;
                          for (const item of selectedRows) {
                            await distributeSupervisor.request({submissionDistributeSupervisorModel: {id: item.id, supervisorId}});
                          }
                          refresh();
                        } catch (e) {
                          message.error(e.message);
                        }
                      }}>
                      确定分配
                    </Button>
                  </Space>
                }>
                <Button>批量分配盲审</Button>
              </Popover>
            )}
            {selectedRows.length > 0 && isBatchDistributeChecker && (
              <Popover
                title={'批量分配论文初审'}
                trigger={'click'}
                content={
                  <Space>
                    <Select value={checkerId} onChange={setCheckerId} style={{width: 200}} placeholder={'请选择论文初审'}>
                      {listUser.data
                        ?.filter(i => i.isChecker)
                        ?.map(u => (
                          <Select.Option key={u.id} value={u.id as number}>
                            {u.name}
                          </Select.Option>
                        ))}
                    </Select>
                    <Button
                      loading={distributeChecker.loading}
                      disabled={!checkerId}
                      onClick={async () => {
                        try {
                          if (!checkerId) return;
                          for (const item of selectedRows) {
                            await distributeChecker.request({submissionDistributeCheckerModel: {id: item.id, checkerId}});
                          }
                          refresh();
                        } catch (e) {
                          message.error(e.message);
                        }
                      }}>
                      确定分配
                    </Button>
                  </Space>
                }>
                <Button>批量分配论文初审</Button>
              </Popover>
            )}
          </Space>
        )}
      </Flex>
      <Table<SubmissionDto>
        dataSource={displayData}
        rowSelection={{
          selectedRowKeys: selectedRows.map(i => i.id as any),
          onChange(_, rows) {
            setSelectedRows(rows);
          },
        }}
        rowKey={'id'}
        bordered
        size={'small'}
        pagination={{defaultPageSize: 20}}>
        {!auth.currentUser?.isJudge && (
          <Table.Column<SubmissionDto>
            title={'学生姓名/学号'}
            width={120}
            render={(_, item) => {
              return (
                <div>
                  <div>{item?.user?.name}</div>
                  <Text type={'secondary'} style={{fontSize: 12}}>
                    {item?.user?.username}
                  </Text>
                </div>
              );
            }}
          />
        )}
        <Table.Column<SubmissionDto>
          title={'批次/状态'}
          width={170}
          render={(_, item) => {
            const count =
              (listState.data
                ?.sort((a, b) => (a.id as number) - (b.id as number))
                ?.filter(i => i.userId === item.userId)
                .findIndex(i => i.id === item.id) || 0) + 1;
            return (
              <Space
                style={{
                  background:
                    item.status && [SubmissionStatus.待上传盲审论文, SubmissionStatus.待分配盲审].includes(item.status) && item.judgeAuditedTime
                      ? '#e8b164'
                      : 'white',
                }}>
                <Badge style={{backgroundColor: count > 1 ? 'red' : 'rgba(82,196,26,0.58)', marginRight: 5, fontSize: 12}} count={count} />
                <div>
                  <SubmissionStatusTag status={item.status as any} />
                  <div style={{fontSize: 12, marginTop: 2}}>{item.term?.name}</div>
                </div>
              </Space>
            );
          }}
        />
        <Table.Column title={'论文标题'} dataIndex={'title'} ellipsis />
        {!auth.currentUser?.isJudge && <Table.Column title={'导师'} width={70} dataIndex={['teacher', 'name']} />}
        {auth.currentUser?.role === UserRole.管理员 && (
          <React.Fragment>
            <Table.Column title={'督导'} width={70} dataIndex={['supervisor', 'name']} />
            <Table.Column title={'专业盲审'} width={80} dataIndex={['judge', 'name']} />
          </React.Fragment>
        )}
        {step === 0 && <Table.Column<SubmissionDto> title={'创建时间'} width={110} render={(_, item) => DateFormat.simple(item.createdTime)} />}
        {step === 3 && <Table.Column<SubmissionDto> title={'结论'} width={50} render={(_, item) => item.supervisorConclusion} />}
        {step === 3 && (
          <Table.Column<SubmissionDto>
            title={'督导审核截止'}
            width={130}
            render={(_, item) => item.supervisorExpiredTime && DateFormat.simple(item.supervisorExpiredTime)}
          />
        )}
        {step === 3 && (
          <Table.Column<SubmissionDto>
            title={'修改截止'}
            width={110}
            render={(_, item) => item.supervisorResubmitExpiredTime && DateFormat.simple(item.supervisorResubmitExpiredTime)}
          />
        )}
        {step === 2 && (
          <Table.Column<SubmissionDto>
            title={'论文初审截止'}
            width={130}
            render={(_, item) => item.checkerExpiredTime && DateFormat.simple(item.checkerExpiredTime)}
          />
        )}
        {step === 2 && (
          <Table.Column<SubmissionDto>
            title={'修改截止'}
            width={130}
            render={(_, item) => item.checkerResubmitExpiredTime && DateFormat.simple(item.checkerResubmitExpiredTime)}
          />
        )}
        {step === 4 && (
          <Table.Column<SubmissionDto>
            title={'盲审审核截止'}
            width={130}
            render={(_, item) => item.judgeExpiredTime && DateFormat.simple(item.judgeExpiredTime)}
          />
        )}
        {step === 4 && (
          <Table.Column<SubmissionDto>
            title={'修改截止'}
            width={130}
            render={(_, item) => item.judgeResubmitExpiredTime && DateFormat.simple(item.judgeResubmitExpiredTime)}
          />
        )}
        <Table.Column<SubmissionDto> title={'最近提交'} width={110} render={(_, item) => DateFormat.simple(item.submittedTime)} />
        <Table.Column<SubmissionDto>
          title={'操作'}
          width={200}
          render={(_, item) => (
            <Space>
              <a onClick={() => history.push('/submission/detail?id=' + item.id)}>详情</a>
              {auth.currentUser?.id === item.teacherId && item.status === SubmissionStatus.导师审核需修改 && <a onClick={() => revoke(item)}>撤销</a>}
              {auth.currentUser?.id === item.supervisorId && item.status === SubmissionStatus.督导审批需修改 && <a onClick={() => revoke(item)}>撤销</a>}
              {auth.currentUser?.id === item.supervisorId && item.status === SubmissionStatus.督导审批不通过 && <a onClick={() => revoke(item)}>撤销</a>}
              {auth.currentUser?.id === item.checkerId && item.status === SubmissionStatus.论文初审不通过 && <a onClick={() => revoke(item)}>撤销</a>}
              {auth.currentUser?.id === item.judgeId && item.status === SubmissionStatus.盲审审批需修改 && <a onClick={() => revoke(item)}>撤销</a>}
              {auth.currentUser?.id === item.judgeId && item.status === SubmissionStatus.盲审审批不通过 && <a onClick={() => revoke(item)}>撤销</a>}
              {auth.currentUser?.id === item.userId && item.status === SubmissionStatus.待导师审核 && <a onClick={() => revoke(item)}>撤销</a>}
              {auth.currentUser?.id === item.userId && item.status === SubmissionStatus.待分配盲审 && <a onClick={() => revoke(item)}>撤销</a>}
              {auth.currentUser?.role === UserRole.管理员 && item.status === SubmissionStatus.待督导审批 && item.superviseSubmitCount == 1 && (
                <a onClick={() => revoke(item)}>撤销</a>
              )}
              {auth.currentUser?.role === UserRole.管理员 && item.status === SubmissionStatus.待盲审审批 && item.judgeSubmitCount == 1 && (
                <a onClick={() => revoke(item)}>撤销</a>
              )}
              {auth.currentUser?.role === UserRole.管理员 &&
                [SubmissionStatus.待上传论文, SubmissionStatus.待导师审核, SubmissionStatus.待督导审批, SubmissionStatus.待盲审审批].includes(
                  item.status as any,
                ) && (
                  <a
                    onClick={() =>
                      sendMessage
                        .request({findParams: {id: item.id}})
                        .then(() => message.success('已发送'))
                        .catch(CommonApiErrorHandler)
                    }>
                    督促邮件
                  </a>
                )}
              {auth.currentUser?.role === UserRole.管理员 && (
                <Popconfirm
                  title={'确认要删除吗?'}
                  onConfirm={() => {
                    removeState
                      .request({findParams: {id: item.id}})
                      .then(() => {
                        message.success('已删除');
                        refresh();
                      })
                      .catch(CommonApiErrorHandler);
                  }}>
                  <a>删除</a>
                </Popconfirm>
              )}
            </Space>
          )}
        />
      </Table>
    </Card>
  );
}
